/** Bind a preset's home-relative x/y offsets to an element's absolute home. */
function absolutizePosition(state, home) {
    const out = { ...state };
    if (state.x !== undefined)
        out.x = home.x + state.x;
    if (state.y !== undefined)
        out.y = home.y + state.y;
    return out;
}
/**
 * Resolve a reusable {@link AnimationPreset} to the concrete `Animation` shape the
 * Epic 05 compiler already consumes (Epic 11 P01, task 1.6). `from`/`to`/`timing`
 * mirror `Animation`, so a preset-sourced animation flows through
 * `generateKeyframes`/`generateAnimationCSS` the same as a hand-authored one.
 *
 * When a `position` is supplied, the preset's home-relative positional `x`/`y` are
 * bound to that element's home (offset by it) so the compiler's delta-from-home
 * convention lands the element AT home — see {@link PresetResolutionContext.position}.
 * `presetSignature` is attached as provenance only and never reaches the keyframes.
 */
export function resolvePresetToAnimation(preset, context) {
    const home = context.position;
    return {
        id: context.id,
        targetElement: context.targetElement,
        scene: context.scene,
        type: preset.type,
        // DBG-019: timing magnitude is a per-instance parameter, so the instruction's
        // requested timing (when supplied) overlays the cached preset's default.
        timing: context.timing ? { ...preset.timing, ...context.timing } : preset.timing,
        from: home ? absolutizePosition(preset.from, home) : preset.from,
        to: home ? absolutizePosition(preset.to, home) : preset.to,
        ...(context.presetSignature !== undefined ? { presetSignature: context.presetSignature } : {}),
    };
}
function propertyStateToCSS(state, position = { x: 0, y: 0 }) {
    const parts = [];
    const transforms = [];
    if (state.x !== undefined)
        transforms.push(`translateX(${state.x - position.x}px)`);
    if (state.y !== undefined)
        transforms.push(`translateY(${state.y - position.y}px)`);
    if (state.scaleX !== undefined || state.scaleY !== undefined) {
        transforms.push(`scale(${state.scaleX ?? 1}, ${state.scaleY ?? 1})`);
    }
    if (state.rotation !== undefined)
        transforms.push(`rotate(${state.rotation}deg)`);
    if (transforms.length > 0) {
        parts.push(`transform: ${transforms.join(' ')}`);
    }
    if (state.opacity !== undefined) {
        parts.push(`opacity: ${state.opacity}`);
    }
    // Handle any extra numeric properties as CSS custom properties
    for (const [key, value] of Object.entries(state)) {
        if (value !== undefined &&
            !['x', 'y', 'scaleX', 'scaleY', 'rotation', 'opacity'].includes(key)) {
            parts.push(`--${key}: ${value}`);
        }
    }
    return parts.join('; ');
}
export function generateKeyframes(animation, position = { x: 0, y: 0 }) {
    const name = sanitizeKeyframeName(animation.id);
    const fromCSS = propertyStateToCSS(animation.from, position);
    const toCSS = propertyStateToCSS(animation.to, position);
    return `@keyframes ${name} {
  from { ${fromCSS} }
  to { ${toCSS} }
}`;
}
/**
 * `scale` proportionally compresses this animation's duration and delay for the
 * production `maxDuration` clamp (Epic 09 P01, task 1.5). `sceneOffset` is the
 * already-scaled cumulative scene offset, so only the in-scene `delay` and the
 * `duration` are scaled here. Default `scale=1` (and `Math.round` of an integer
 * is that integer) keeps preview output BYTE-IDENTICAL.
 */
export function generateAnimationCSS(animation, sceneOffset, scale = 1) {
    const name = sanitizeKeyframeName(animation.id);
    const duration = Math.round(animation.timing.duration * scale);
    const totalDelay = sceneOffset + Math.round(animation.timing.delay * scale);
    const easing = mapEasing(animation.timing.easing);
    // Loop parts are emitted ONLY when present, so a single-shot animation's CSS is
    // byte-identical to what it was before loops existed.
    const { iterations, direction } = animation.timing;
    const loop = [
        iterations !== undefined ? `${iterations}` : '',
        direction !== undefined ? direction : '',
    ]
        .filter(Boolean)
        .join(' ');
    return `animation: ${name} ${duration}ms ${easing} ${totalDelay}ms${loop ? ` ${loop}` : ''} both`;
}
function mapEasing(easing) {
    switch (easing) {
        case 'ease-in':
            return 'ease-in';
        case 'ease-out':
            return 'ease-out';
        case 'ease-in-out':
            return 'ease-in-out';
        case 'linear':
            return 'linear';
        default:
            // Pass through cubic-bezier and other CSS-valid values
            return easing;
    }
}
function sanitizeKeyframeName(id) {
    return sanitizeId(id);
}
export function sanitizeId(id) {
    return id.replace(/[^a-zA-Z0-9_-]/g, '_');
}
export { propertyStateToCSS };
